import React, { useState, useRef } from "react";

import CustomModal from "@components/CustomModal";

import api from "@helpers/api";

import "./styles.scss";

import { dispatchEvent } from "@hooks/useEvent";

import {
    useUploadCommunityAsset,
    AssetCategory,
    isValidHttpUrl,
} from "@features/uploadAsset";

import { Text, Button } from "@fragments";
import { DownloadRounded, TuneRounded } from "@mui/icons-material";

import videoHelpers from "@helpers/video";
import EVENTS from "./events";

import Video from "./sections/Video";
import Title from "./sections/Title";
import Description from "./sections/Description";
import LessonOwner from "./sections/LessonOwner";
import SelectCategories from "./sections/SelectCategories";
import Attachments from "./sections/Attachments";
import Availability from "./sections/Availability";
import WaitDaysAfterPayment from "./sections/WaitDaysAfterPayment";
import Launch from "./sections/Launch";

async function dataUrlToFile(dataUrl, filename) {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

function focusFieldTarget(ref) {
    if (!ref?.current) return;
    requestAnimationFrame(() => {
        const el = ref.current;
        el.scrollIntoView?.({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") {
            try {
                el.focus({ preventScroll: true });
            } catch {
                el.focus();
            }
        }
    });
}

export default function ManageContentModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadBusyCount, setUploadBusyCount] = useState(0);

    const REF_config = useRef({});
    const REF_callback = useRef();

    const refFocusTitle = useRef(null);
    const refFocusDescription = useRef(null);
    const refFocusVideoSection = useRef(null);
    const refFocusAvailability = useRef(null);

    const handleUploadBusyChange = (uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    };

    modalRef.current = {
        open(preFill, callback = () => { }) {
            const bannerRaw = preFill.banner;
            const banner =
                bannerRaw != null && String(bannerRaw).trim()
                    ? String(bannerRaw).trim()
                    : null;

            REF_config.current = {
                ...preFill,
                availability: preFill.availability || {},
                video: {
                    aspect_ratio: preFill.video?.aspect_ratio || 16 / 9,
                    src: preFill.video?.src || null,
                },
                banner,
            };
            delete REF_config.current.banner_base64;
            REF_callback.current = callback;
            setOpen(true);
        },
    };

    function validateContentForm() {
        const { name, description, video, availability } = REF_config.current;
        const trimmedName = name?.trim?.() ?? "";

        if (!trimmedName || trimmedName.length < 3 || trimmedName.length > 100) {
            window.setSnackbar(
                "Insira um nome válido (entre 3 e 100 caracteres)."
            );
            focusFieldTarget(refFocusTitle);
            return false;
        }

        if (description != null && description.length > 1000) {
            window.setSnackbar(
                "Insira uma descrição de no máximo 1.000 caracteres."
            );
            focusFieldTarget(refFocusDescription);
            return false;
        }

        const videoSrc = typeof video === "string" ? video : video?.src;
        if (!videoSrc) {
            window.setSnackbar(
                "Adicione um vídeo e aguarde o processamento."
            );
            focusFieldTarget(refFocusVideoSection);
            return false;
        }

        const plans = availability?.plans;
        if (Array.isArray(plans) && plans.length === 0) {
            window.setSnackbar(
                "Selecione pelo menos um plano ou deixe o conteúdo disponível publicamente."
            );
            focusFieldTarget(refFocusAvailability);
            return false;
        }

        return true;
    }

    async function handleSubmit() {
        try {
            if (!validateContentForm()) {
                return;
            }

            const payload = getPayload();

            setLoading(true);

            const contentId = REF_config.current.id;
            const { data } = await (contentId ? api.patch(`content/${contentId}`, payload) : api.post("content", payload));

            if (data.id) {
                await REF_callback.current(data);
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
            window.setSnackbar("Houve um erro ao salvar o conteúdo");
        }

        setLoading(false);

        function getPayload() {
            const {
                id,
                name,
                description,
                availability,
                section,
                playlist,
                video,
                attachments,
                creator,
                categories,
                banner,
                timestamps
            } = REF_config.current;

            const data = {
                section: parseInt(section),
                name: name?.trim(),
                description: description?.trim(),
                creator: creator?.id,
                video: {
                    src: typeof video === 'string' ? video : video?.src,
                    aspect_ratio: video?.aspect_ratio
                },
                availability: {
                    wait_days_after_purchase: availability.wait_days_after_purchase,
                    plans: availability.plans ? availability.plans[0] === 'all' ? null : availability.plans : null,
                    launch_at: availability?.launch_at || timestamps?.launch
                },
                attachments: attachments || [],
                categories: categories || [],
            };

            if (banner && isValidHttpUrl(banner)) {
                data.banner = banner;
            }

            if (!!playlist) {
                data.playlist = parseInt(playlist);
            }

            if (!!id) {
                data.id = id;
            }

            return data;
        }
    }

    async function handleDelete() {
        try {
            const { data } = await api.delete(`content/${REF_config.current.id}`);
            window.setSnackbar("Conteúdo apagado com sucesso");
            REF_callback.current();
            setOpen(false);
        } catch (e) {
            console.log(e);
        }
    }

    async function handleDownload() {
        try {
            const source = REF_config.current.video.src;

            if (!source) {
                window.setSnackbar('Nenhum vídeo disponível para download');
                return;
            }

            // Check if the source has .m3u8 extension
            if (!source.endsWith('.m3u8')) {
                // If it's not .m3u8, download as is
                downloadFile(source);
                return;
            }

            // Replace .m3u8 with .mp4 or .mov to get the original video
            const baseUrl = source.replace('.m3u8', '');
            const mp4Url = `${baseUrl}.mp4`;
            const movUrl = `${baseUrl}.mov`;

            // Try .mp4 first
            const mp4Exists = await checkFileExists(mp4Url);
            if (mp4Exists) {
                await downloadFile(mp4Url);
                return;
            }

            // Try .mov if .mp4 doesn't exist
            const movExists = await checkFileExists(movUrl);

            if (movExists) {
                await downloadFile(movUrl);
                return;
            }

            // If neither exists, show error
            window.setSnackbar('Vídeo original não encontrado');
        } catch (e) {
            console.log(e);
            window.setSnackbar('Erro ao fazer download do vídeo');
        }
    }

    async function checkFileExists(url) {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
        } catch (e) {
            return false;
        }
    }

    async function downloadFile(url) {
        try {
            window.setSnackbar('Seu vídeo será baixado em breve, não feche esta tela');

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);

            window.setSnackbar('Seu vídeo está sendo baixado');
        } catch (e) {
            console.error('Download error:', e);
            window.setSnackbar('Erro ao fazer download do vídeo');
        }
    }

    return (
        <>
            <CustomModal
                maxWidth={670}
                bottomButtonSumbitText="Salvar conteúdo"
                bottomButtonCancelText="Cancelar"
                loading={loading}
                disabled={uploadBusyCount > 0}
                title="Editar conteúdo"
                deletionTitle="Você realmente deseja apagar este conteúdo permanentemente?"
                handleDelete={handleDelete}
                handleSubmit={handleSubmit}
                topActions={
                    REF_config.current.id && REF_config.current?.video?.src && (
                        <Button
                            onClick={handleDownload}
                            color="overlay"
                            startIcon={<DownloadRounded fontSize="small" />}
                            className="mr-2"
                        >
                            Download
                        </Button>
                    )}
                open={open}
                handleClose={() => setOpen(false)}
                bodyComponent={
                    open ? (
                        <Body
                            configRef={REF_config}
                            onUploadBusyChange={handleUploadBusyChange}
                            refFocusTitle={refFocusTitle}
                            refFocusDescription={refFocusDescription}
                            refFocusVideoSection={refFocusVideoSection}
                            refFocusAvailability={refFocusAvailability}
                        />
                    ) : null
                }
            />
        </>
    );
}



function Body({
    configRef,
    onUploadBusyChange,
    refFocusTitle,
    refFocusDescription,
    refFocusVideoSection,
    refFocusAvailability,
}) {
    const [, setTick] = useState(0);
    const bump = () => setTick((n) => n + 1);

    const communityId = configRef.current.community
        ? parseInt(configRef.current.community, 10)
        : null;

    const { uploadFile } = useUploadCommunityAsset({
        communityId,
        onUploadingChange: onUploadBusyChange,
    });

    function handleBannerUrlChange(url) {
        configRef.current.banner = url;
        configRef.current.banner_base64 = undefined;
        bump();
    }

    async function handleVideoUploaded(videoInfo) {
        configRef.current.video = { ...configRef.current.video, src: videoInfo.video.path };
        dispatchEvent(EVENTS.VIDEO_URL_CHANGE, videoInfo.video.path);
    }

    function handleRemoveVideo() {
        configRef.current.video = { ...configRef.current.video, src: null };
        dispatchEvent(EVENTS.VIDEO_URL_CHANGE, null);
    }

    async function handleGenerateThumb(path) {
        const video = document.createElement("video");
        video.src = path;
        video.onloadedmetadata = () => {
            const ratio = video.videoWidth / video.videoHeight;
            const diffHorizontal = Math.abs(ratio - 1.78);
            const diffVertical = Math.abs(ratio - 0.56);

            const layout = diffVertical < diffHorizontal ? 0.56 : 1.78;
            handlerController.handleChangeAspectRatio(layout);
        };

        if (configRef.current.banner) return;
        const result = await videoHelpers.generateThumbFromLocalVideo(path);
        if (!result || !communityId) return;

        try {
            const file = await dataUrlToFile(result, "thumb.jpg");
            const url = await uploadFile(file, AssetCategory.CONTENT_BANNER);
            configRef.current.banner = url;
            configRef.current.banner_base64 = undefined;
            bump();
        } catch (e) {
            console.error(e);
            window.setSnackbar?.(
                "Não foi possível enviar o banner gerado. Escolha uma imagem manualmente."
            );
        }
    }

    const handlerController = {
        handleChangeName(value) {
            configRef.current.name = value;
        },
        handleChangeDescription(value) {
            configRef.current.description = value;
        },
        handleChangeOwner(value) {
            configRef.current.creator = { id: value };
        },

        handleChangeCategories(value) {
            configRef.current.categories = value;
        },

        handleChangeAspectRatio(value) {
            configRef.current.video.aspect_ratio = value;
            dispatchEvent(EVENTS.ASPECT_RATIO_CHANGE, value);
        },
        handleChangeAttachments(value) {
            configRef.current.attachments = value;
        },
        handleChangeAvailability(value) {
            configRef.current.availability.plans = value;
            dispatchEvent(EVENTS.AVAILABILITY_CHANGE, value);
        },
        handleChangeWaitDays(value) {
            configRef.current.availability.wait_days_after_purchase = value;
        },
        handleChangeLaunch(value) {
            configRef.current.timestamps.launch = value;
        }
    };

    return (
        <div className="lesson-config-box mt-1 px-4 py-1">
            <Video
                sectionRef={refFocusVideoSection}
                communityId={communityId}
                bannerUrl={configRef.current.banner}
                onBannerChange={handleBannerUrlChange}
                onBannerUploadingChange={onUploadBusyChange}
                defaultAspectRatio={configRef.current.video?.aspect_ratio}
                defaultVideoUrl={configRef.current.video?.src}
                onVideoUploaded={handleVideoUploaded}
                onRemoveVideo={handleRemoveVideo}
                onGenerateThumb={handleGenerateThumb}
            />

            <Text.h3
                value="Configurações gerais"
                left={<TuneRounded />}
                className="mb-2 mt-3"
            />
            <Title
                ref={refFocusTitle}
                value={configRef.current.name}
                onInput={handlerController.handleChangeName}
            />
            <Description
                ref={refFocusDescription}
                value={configRef.current.description}
                onInput={handlerController.handleChangeDescription}
            />

            <LessonOwner
                key={`${configRef.current.community}-${configRef.current.creator?.id}`}
                value={configRef.current.creator?.id}
                onChange={handlerController.handleChangeOwner}
                community={configRef.current.community}
            />

            <SelectCategories
                value={configRef.current.categories}
                onChange={handlerController.handleChangeCategories}
            />

            <Attachments
                value={configRef.current.attachments}
                onChange={handlerController.handleChangeAttachments}
            />

            <div ref={refFocusAvailability} tabIndex={-1}>
                <Availability
                    value={configRef.current.availability}
                    onChange={handlerController.handleChangeAvailability}
                    community={configRef.current.community}
                />
            </div>

            <WaitDaysAfterPayment
                value={configRef.current.availability?.wait_days_after_purchase}
                onChange={handlerController.handleChangeWaitDays}
                initialHasPlans={configRef.current.availability?.plans?.length > 0}
            />

            <Launch
                value={configRef.current.timestamps?.launch}
                onChange={handlerController.handleChangeLaunch}
            />
        </div>
    );
}
